import React, { useEffect, useState } from 'react';
import { DataTable } from '../components/DataTable';
import { WizardForm } from '../components/WizardForm';
import { StatusBadge } from '../components/StatusBadge';
import { UserIcon } from 'lucide-react';
import { beneficiaries as initialBeneficiaries } from '../data/beneficiaries';
// Helper to calculate age
const calculateAge = (dob: string) => {
  if (!dob) return '';
  const birthDate = new Date(dob);
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const m = today.getMonth() - birthDate.getMonth();
  if (m < 0 || m === 0 && today.getDate() < birthDate.getDate()) {
    age--;
  }
  return age.toString();
};
// ID Generation Helpers
const DEPT_CODE = '1';
const RANGE_CODES: Record<string, string> = {
  'Satkhira Range': '01',
  'Khulna Range': '02',
  'Chandpai Range': '03',
  'Sarankhola Range': '04'
};
const STATION_CODES: Record<string, string> = {
  'Burigoalini Station': '01',
  'Kaikhali Station': '02',
  'Dobeki Station': '03',
  'Nalian Station': '04',
  'Patkosta Station': '05',
  'Karamjal Station': '06',
  'Harbaria Station': '07',
  'Joymoni Station': '08',
  'Tamulbunia Station': '09',
  'Supati Station': '10',
  'Sharankhola Station': '11',
  'Kotka Station': '12'
};
const generateBeneficiaryId = (range: string, station: string, sequence: number) => {
  const rCode = RANGE_CODES[range] || '00';
  const sCode = STATION_CODES[station] || '00';
  const seq = String(sequence).padStart(5, '0');
  return `${DEPT_CODE}-${rCode}-${sCode}-${seq}`;
};
// Add generated IDs to initial data
const beneficiariesWithIds = initialBeneficiaries.map((b, index) => ({
  ...b,
  beneficiaryId: generateBeneficiaryId('Satkhira Range', 'Burigoalini Station', index + 1)
}));
export function BeneficiaryRegistry() {
  const [activeTab, setActiveTab] = useState<'list' | 'add' | 'approval'>('list');
  const [currentStep, setCurrentStep] = useState(0);
  const [beneficiariesList, setBeneficiariesList] = useState(beneficiariesWithIds);
  // Form State
  const [formData, setFormData] = useState({
    photo: '',
    fullName: '',
    fatherName: '',
    spouseName: '',
    nid: '',
    birthCertNo: '',
    dob: '',
    age: '',
    gender: '',
    education: '',
    professions: [] as string[],
    otherProfession: '',
    groupName: 'Eco Tour - Mangrove Trail',
    prevPermit: 'PER-2023-089, PER-2024-012',
    division: '',
    district: '',
    upazila: '',
    thana: '',
    union: '',
    village: '',
    range: '',
    station: '',
    bloodGroup: '',
    phone: '',
    email: '',
    nidAttachment: '',
    birthCertAttachment: '',
    educationAttachment: ''
  });
  // Auto-calculate age when DOB changes
  useEffect(() => {
    if (formData.dob) {
      setFormData(prev => ({
        ...prev,
        age: calculateAge(prev.dob)
      }));
    }
  }, [formData.dob]);
  const stationsByRange: Record<string, string[]> = {
    'Satkhira Range': ['Burigoalini Station', 'Kaikhali Station', 'Dobeki Station'],
    'Khulna Range': ['Nalian Station', 'Patkosta Station', 'Karamjal Station'],
    'Chandpai Range': ['Harbaria Station', 'Joymoni Station', 'Tamulbunia Station'],
    'Sarankhola Range': ['Supati Station', 'Sharankhola Station', 'Kotka Station']
  };
  const professionOptions = ['Crab Collector', 'Fish Collector', 'Honey Collector', 'Leaf Collector', 'Salt Water Collector', 'Sun Grass Collector', 'Others'];
  const handleProfessionChange = (prof: string) => {
    setFormData(prev => {
      const current = prev.professions;
      if (current.includes(prof)) {
        return {
          ...prev,
          professions: current.filter(p => p !== prof)
        };
      } else {
        return {
          ...prev,
          professions: [...current, prof]
        };
      }
    });
  };
  const pendingApprovals = [{
    photo: '👤',
    name: 'Nur Islam',
    nid: '6789012345678',
    profession: 'Fish Collector',
    village: 'Munshiganj',
    date: '2024-03-30'
  }, {
    photo: '👤',
    name: 'Habib Mollah',
    nid: '7890123456789',
    profession: 'Wood Collector',
    village: 'Gabura',
    date: '2024-03-29'
  }];
  const columns = [{
    key: 'beneficiaryId',
    label: 'Beneficiary ID'
  }, {
    key: 'name',
    label: 'Display Name'
  }, {
    key: 'email',
    label: 'Email ID'
  }, {
    key: 'nid',
    label: 'ID Number'
  }, {
    key: 'phone',
    label: 'Phone Number'
  }, {
    key: 'status',
    label: 'Status',
    render: (value: string) => <StatusBadge status={value as any} data-id="element-188" />
  }];
  const approvalColumns = [{
    key: 'photo',
    label: 'Photo',
    render: (value: string) => <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center text-xl" data-id="element-189">
          {value}
        </div>
  }, {
    key: 'name',
    label: 'Name'
  }, {
    key: 'nid',
    label: 'NID'
  }, {
    key: 'profession',
    label: 'Profession'
  }, {
    key: 'village',
    label: 'Village'
  }, {
    key: 'date',
    label: 'Applied Date'
  }];
  const filters = [{
    key: 'profession',
    label: 'Profession',
    options: ['Fish Collector', 'Honey Collector', 'Wood Collector', 'Crab Collector']
  }, {
    key: 'status',
    label: 'Status',
    options: ['Approved', 'Pending', 'Rejected']
  }, {
    key: 'village',
    label: 'Village',
    options: ['Munshiganj', 'Gabura', 'Shyamnagar', 'Koyra', 'Dacope']
  }];
  const wizardSteps = [{
    title: 'Personal Info',
    description: 'Enter beneficiary personal information'
  }, {
    title: 'Address & Contact',
    description: 'Enter address and contact details'
  }, {
    title: 'Professional Details',
    description: 'Enter professional information'
  }, {
    title: 'Documents',
    description: 'Upload required documents'
  }, {
    title: 'Review & Submit',
    description: 'Review all information before submitting'
  }];
  return <div className="space-y-6" data-id="element-190">
      <div data-id="element-191">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-192">
          Beneficiary Management
        </h1>
        <p className="text-sm text-gray-600 mt-1" data-id="element-193">
          Manage resource collectors (Jele, Moual, Bawali)
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200" data-id="element-194">
        <div className="flex gap-4" data-id="element-195">
          <button onClick={() => setActiveTab('list')} className={`px-4 py-2 border-b-2 font-medium text-sm transition-colors ${activeTab === 'list' ? 'border-[#1B5E20] text-[#1B5E20]' : 'border-transparent text-gray-600 hover:text-gray-900'}`} data-id="element-196">
            Beneficiary List
          </button>
          <button onClick={() => setActiveTab('add')} className={`px-4 py-2 border-b-2 font-medium text-sm transition-colors ${activeTab === 'add' ? 'border-[#1B5E20] text-[#1B5E20]' : 'border-transparent text-gray-600 hover:text-gray-900'}`} data-id="element-197">
            Add Beneficiary
          </button>
          <button onClick={() => setActiveTab('approval')} className={`px-4 py-2 border-b-2 font-medium text-sm transition-colors ${activeTab === 'approval' ? 'border-[#1B5E20] text-[#1B5E20]' : 'border-transparent text-gray-600 hover:text-gray-900'}`} data-id="element-198">
            Approval Queue ({pendingApprovals.length})
          </button>
        </div>
      </div>

      {/* Content */}
      {activeTab === 'list' && <DataTable columns={columns} data={beneficiariesList} filters={filters} onDataChange={setBeneficiariesList} searchPlaceholder="Search by name or NID..." data-id="element-199" />}

      {activeTab === 'add' && <WizardForm steps={wizardSteps} currentStep={currentStep} onStepChange={step => {
      // Validation before moving to next step
      if (currentStep === 0 && step > 0) {
        if (!formData.nid && !formData.birthCertNo) {
          alert('Either NID or Birth Certificate No. is mandatory.');
          return;
        }
        if (formData.age && parseInt(formData.age) < 18) {
          alert('Age cannot be below 18.');
          return;
        }
      }
      setCurrentStep(step);
    }} onSubmit={() => {
      if (!formData.fullName || !formData.nid && !formData.birthCertNo || formData.professions.length === 0) {
        alert('Please fill in all required fields.');
        return;
      }
      if (parseInt(formData.age) < 18) {
        alert('Age cannot be below 18.');
        return;
      }
      const newId = generateBeneficiaryId(formData.range, formData.station, beneficiariesList.length + 1);
      const newBeneficiary = {
        beneficiaryId: newId,
        photo: '👤',
        name: formData.fullName,
        email: formData.email,
        nid: formData.nid || formData.birthCertNo,
        phone: formData.phone,
        profession: formData.professions.join(', '),
        village: formData.village,
        status: 'pending' as const,
        date: new Date().toISOString().split('T')[0]
      };
      setBeneficiariesList([newBeneficiary, ...beneficiariesList]);
      alert(`Beneficiary ${formData.fullName} registered successfully with ID ${newId} and sent for approval!`);
      setCurrentStep(0);
      setActiveTab('approval');
      // Reset form
      setFormData({
        photo: '',
        fullName: '',
        fatherName: '',
        spouseName: '',
        nid: '',
        birthCertNo: '',
        dob: '',
        age: '',
        gender: '',
        education: '',
        professions: [],
        otherProfession: '',
        groupName: 'Eco Tour - Mangrove Trail',
        prevPermit: 'PER-2023-089, PER-2024-012',
        division: '',
        district: '',
        upazila: '',
        thana: '',
        union: '',
        village: '',
        range: '',
        station: '',
        bloodGroup: '',
        phone: '',
        email: '',
        nidAttachment: '',
        birthCertAttachment: '',
        educationAttachment: ''
      });
    }} data-id="element-200">
          {currentStep === 0 && <div className="space-y-4" data-id="element-201">
              <div className="grid grid-cols-2 gap-4" data-id="element-202">
                <div data-id="element-203">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-204">
                    Photo Upload *
                  </label>
                  <input type="file" accept="image/*" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-205" />
                </div>
                <div data-id="element-206">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-207">
                    Full Name *
                  </label>
                  <input type="text" required value={formData.fullName} onChange={e => setFormData({
              ...formData,
              fullName: e.target.value
            })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-208" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4" data-id="element-209">
                <div data-id="element-210">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-211">
                    Father's Name *
                  </label>
                  <input type="text" required value={formData.fatherName} onChange={e => setFormData({
              ...formData,
              fatherName: e.target.value
            })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-212" />
                </div>
                <div data-id="element-213">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-214">
                    Spouse Name
                  </label>
                  <input type="text" value={formData.spouseName} onChange={e => setFormData({
              ...formData,
              spouseName: e.target.value
            })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-215" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4" data-id="element-216">
                <div data-id="element-217">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-218">
                    NID Number{' '}
                    <span className="text-xs text-gray-500 font-normal" data-id="element-219">
                      (Required if no Birth Cert)
                    </span>
                  </label>
                  <input type="text" value={formData.nid} onChange={e => setFormData({
              ...formData,
              nid: e.target.value
            })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-220" />
                </div>
                <div data-id="element-221">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-222">
                    Birth Certificate No.{' '}
                    <span className="text-xs text-gray-500 font-normal" data-id="element-223">
                      (Required if no NID)
                    </span>
                  </label>
                  <input type="text" value={formData.birthCertNo} onChange={e => setFormData({
              ...formData,
              birthCertNo: e.target.value
            })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-224" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4" data-id="element-225">
                <div data-id="element-226">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-227">
                    Date of Birth *
                  </label>
                  <input type="date" required value={formData.dob} onChange={e => setFormData({
              ...formData,
              dob: e.target.value
            })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-228" />
                </div>
                <div data-id="element-229">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-230">
                    Age (Auto-calculated)
                  </label>
                  <input type="number" readOnly value={formData.age} className={`w-full px-3 py-2 border rounded-lg focus:outline-none bg-gray-50 ${formData.age && parseInt(formData.age) < 18 ? 'border-red-500 text-red-600' : 'border-gray-300 text-gray-600'}`} data-id="element-231" />
                  {formData.age && parseInt(formData.age) < 18 && <p className="text-xs text-red-500 mt-1" data-id="element-232">
                      Age must be 18 or above.
                    </p>}
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4" data-id="element-233">
                <div data-id="element-234">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-235">
                    Gender *
                  </label>
                  <select required value={formData.gender} onChange={e => setFormData({
              ...formData,
              gender: e.target.value
            })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-236">
                    <option value="" data-id="element-237">Select Gender</option>
                    <option value="male" data-id="element-238">Male</option>
                    <option value="female" data-id="element-239">Female</option>
                    <option value="other" data-id="element-240">Other</option>
                  </select>
                </div>
                <div data-id="element-241">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-242">
                    Blood Group
                  </label>
                  <select value={formData.bloodGroup} onChange={e => setFormData({
              ...formData,
              bloodGroup: e.target.value
            })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-243">
                    <option value="" data-id="element-244">Select Blood Group</option>
                    <option value="A+" data-id="element-245">A+</option>
                    <option value="A-" data-id="element-246">A-</option>
                    <option value="B+" data-id="element-247">B+</option>
                    <option value="B-" data-id="element-248">B-</option>
                    <option value="AB+" data-id="element-249">AB+</option>
                    <option value="AB-" data-id="element-250">AB-</option>
                    <option value="O+" data-id="element-251">O+</option>
                    <option value="O-" data-id="element-252">O-</option>
                  </select>
                </div>
                <div data-id="element-253">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-254">
                    Education
                  </label>
                  <input type="text" value={formData.education} onChange={e => setFormData({
              ...formData,
              education: e.target.value
            })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-255" />
                </div>
              </div>
            </div>}
          {currentStep === 1 && <div className="space-y-4" data-id="element-256">
              <div className="grid grid-cols-2 gap-4" data-id="element-257">
                <div data-id="element-258">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-259">
                    Division *
                  </label>
                  <select required value={formData.division} onChange={e => setFormData({
              ...formData,
              division: e.target.value
            })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-260">
                    <option value="" data-id="element-261">Select Division</option>
                    <option value="Khulna" data-id="element-262">Khulna</option>
                    <option value="Barisal" data-id="element-263">Barisal</option>
                    <option value="Dhaka" data-id="element-264">Dhaka</option>
                  </select>
                </div>
                <div data-id="element-265">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-266">
                    District *
                  </label>
                  <select required value={formData.district} onChange={e => setFormData({
              ...formData,
              district: e.target.value
            })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-267">
                    <option value="" data-id="element-268">Select District</option>
                    <option value="Satkhira" data-id="element-269">Satkhira</option>
                    <option value="Khulna" data-id="element-270">Khulna</option>
                    <option value="Bagerhat" data-id="element-271">Bagerhat</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4" data-id="element-272">
                <div data-id="element-273">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-274">
                    Upazila *
                  </label>
                  <select required value={formData.upazila} onChange={e => setFormData({
              ...formData,
              upazila: e.target.value
            })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-275">
                    <option value="" data-id="element-276">Select Upazila</option>
                    <option value="Shyamnagar" data-id="element-277">Shyamnagar</option>
                    <option value="Koyra" data-id="element-278">Koyra</option>
                    <option value="Dacope" data-id="element-279">Dacope</option>
                  </select>
                </div>
                <div data-id="element-280">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-281">
                    Thana *
                  </label>
                  <select required value={formData.thana} onChange={e => setFormData({
              ...formData,
              thana: e.target.value
            })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-282">
                    <option value="" data-id="element-283">Select Thana</option>
                    <option value="Shyamnagar Thana" data-id="element-284">Shyamnagar Thana</option>
                    <option value="Koyra Thana" data-id="element-285">Koyra Thana</option>
                    <option value="Dacope Thana" data-id="element-286">Dacope Thana</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4" data-id="element-287">
                <div data-id="element-288">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-289">
                    Union *
                  </label>
                  <input type="text" required value={formData.union} onChange={e => setFormData({
              ...formData,
              union: e.target.value
            })} placeholder="Enter Union name" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-290" />
                </div>
                <div data-id="element-291">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-292">
                    Village *
                  </label>
                  <input type="text" required value={formData.village} onChange={e => setFormData({
              ...formData,
              village: e.target.value
            })} placeholder="Enter Village name" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-293" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4" data-id="element-294">
                <div data-id="element-295">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-296">
                    Range *
                  </label>
                  <select required value={formData.range} onChange={e => setFormData({
              ...formData,
              range: e.target.value,
              station: ''
            })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-297">
                    <option value="" data-id="element-298">Select Range</option>
                    <option value="Satkhira Range" data-id="element-299">Satkhira Range</option>
                    <option value="Khulna Range" data-id="element-300">Khulna Range</option>
                    <option value="Chandpai Range" data-id="element-301">Chandpai Range</option>
                    <option value="Sarankhola Range" data-id="element-302">Sarankhola Range</option>
                  </select>
                </div>
                <div data-id="element-303">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-304">
                    Station
                  </label>
                  <select value={formData.station} onChange={e => setFormData({
              ...formData,
              station: e.target.value
            })} disabled={!formData.range} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20] disabled:bg-gray-100 disabled:text-gray-400" data-id="element-305">
                    <option value="" data-id="element-306">
                      {formData.range ? 'Select Station' : 'Select Range first'}
                    </option>
                    {formData.range && stationsByRange[formData.range]?.map(station => <option key={station} value={station} data-id="element-307">
                          {station}
                        </option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4" data-id="element-308">
                <div data-id="element-309">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-310">
                    Phone Number *
                  </label>
                  <input type="tel" required value={formData.phone} onChange={e => setFormData({
              ...formData,
              phone: e.target.value
            })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-311" />
                </div>
                <div data-id="element-312">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-313">
                    Email Address
                  </label>
                  <input type="email" value={formData.email} onChange={e => setFormData({
              ...formData,
              email: e.target.value
            })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-314" />
                </div>
              </div>
            </div>}
          {currentStep === 2 && <div className="space-y-6" data-id="element-315">
              <div data-id="element-316">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-317">
                  Profession(s) *
                </label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-id="element-318">
                  {professionOptions.map(prof => <label key={prof} className="flex items-center gap-2 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50" data-id="element-319">
                      <input type="checkbox" checked={formData.professions.includes(prof)} onChange={() => handleProfessionChange(prof)} className="w-4 h-4 text-[#1B5E20] rounded focus:ring-[#1B5E20]" data-id="element-320" />
                      <span className="text-sm text-gray-700" data-id="element-321">{prof}</span>
                    </label>)}
                </div>
                {formData.professions.includes('Others') && <div className="mt-3" data-id="element-322">
                    <input type="text" placeholder="Please specify other profession" value={formData.otherProfession} onChange={e => setFormData({
              ...formData,
              otherProfession: e.target.value
            })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-323" />
                  </div>}
              </div>
              <div className="grid grid-cols-2 gap-4" data-id="element-324">
                <div data-id="element-325">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-326">
                    Conservation Group / Association
                  </label>
                  <input type="text" readOnly value={formData.groupName} className="w-full px-3 py-2 border border-gray-200 bg-gray-50 rounded-lg text-gray-600 focus:outline-none" data-id="element-327" />
                  <p className="text-xs text-gray-500 mt-1" data-id="element-328">
                    Tagged conservation group (Read-only)
                  </p>
                </div>
                <div data-id="element-329">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-330">
                    Previous Permit Records
                  </label>
                  <input type="text" readOnly value={formData.prevPermit} className="w-full px-3 py-2 border border-gray-200 bg-gray-50 rounded-lg text-gray-600 focus:outline-none" data-id="element-331" />
                  <p className="text-xs text-gray-500 mt-1" data-id="element-332">
                    Past permits for this beneficiary (Read-only)
                  </p>
                </div>
              </div>
            </div>}
          {currentStep === 3 && <div className="space-y-4" data-id="element-333">
              <div data-id="element-334">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-335">
                  NID Copy
                </label>
                <input type="file" accept="image/*,application/pdf" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-336" />
              </div>
              <div data-id="element-337">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-338">
                  Birth Certificate Copy
                </label>
                <input type="file" accept="image/*,application/pdf" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-339" />
              </div>
              <div data-id="element-340">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-341">
                  Education Certificate
                </label>
                <input type="file" accept="image/*,application/pdf" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-342" />
              </div>
            </div>}
          {currentStep === 4 && <div className="space-y-6" data-id="element-343">
              <div className="bg-gray-50 rounded-lg p-4" data-id="element-344">
                <div className="flex items-center justify-between mb-3" data-id="element-345">
                  <h4 className="font-semibold text-gray-900" data-id="element-346">
                    Personal Information
                  </h4>
                  <button onClick={() => setCurrentStep(0)} className="text-sm text-[#1B5E20] hover:underline" data-id="element-347">
                    Edit
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm" data-id="element-348">
                  <div data-id="element-349">
                    <span className="text-gray-600" data-id="element-350">Name:</span>{' '}
                    <span className="font-medium" data-id="element-351">
                      {formData.fullName || 'Not provided'}
                    </span>
                  </div>
                  <div data-id="element-352">
                    <span className="text-gray-600" data-id="element-353">Father's Name:</span>{' '}
                    <span className="font-medium" data-id="element-354">
                      {formData.fatherName || 'Not provided'}
                    </span>
                  </div>
                  <div data-id="element-355">
                    <span className="text-gray-600" data-id="element-356">NID:</span>{' '}
                    <span className="font-medium" data-id="element-357">
                      {formData.nid || 'Not provided'}
                    </span>
                  </div>
                  <div data-id="element-358">
                    <span className="text-gray-600" data-id="element-359">Birth Cert:</span>{' '}
                    <span className="font-medium" data-id="element-360">
                      {formData.birthCertNo || 'Not provided'}
                    </span>
                  </div>
                  <div data-id="element-361">
                    <span className="text-gray-600" data-id="element-362">DOB:</span>{' '}
                    <span className="font-medium" data-id="element-363">
                      {formData.dob || 'Not provided'}
                    </span>
                  </div>
                  <div data-id="element-364">
                    <span className="text-gray-600" data-id="element-365">Age:</span>{' '}
                    <span className="font-medium" data-id="element-366">
                      {formData.age || 'Not provided'}
                    </span>
                  </div>
                  <div data-id="element-367">
                    <span className="text-gray-600" data-id="element-368">Blood Group:</span>{' '}
                    <span className="font-medium" data-id="element-369">
                      {formData.bloodGroup || 'Not provided'}
                    </span>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 rounded-lg p-4" data-id="element-370">
                <div className="flex items-center justify-between mb-3" data-id="element-371">
                  <h4 className="font-semibold text-gray-900" data-id="element-372">
                    Address & Contact
                  </h4>
                  <button onClick={() => setCurrentStep(1)} className="text-sm text-[#1B5E20] hover:underline" data-id="element-373">
                    Edit
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm" data-id="element-374">
                  <div data-id="element-375">
                    <span className="text-gray-600" data-id="element-376">Division:</span>{' '}
                    <span className="font-medium" data-id="element-377">
                      {formData.division || 'Not provided'}
                    </span>
                  </div>
                  <div data-id="element-378">
                    <span className="text-gray-600" data-id="element-379">District:</span>{' '}
                    <span className="font-medium" data-id="element-380">
                      {formData.district || 'Not provided'}
                    </span>
                  </div>
                  <div data-id="element-381">
                    <span className="text-gray-600" data-id="element-382">Upazila:</span>{' '}
                    <span className="font-medium" data-id="element-383">
                      {formData.upazila || 'Not provided'}
                    </span>
                  </div>
                  <div data-id="element-384">
                    <span className="text-gray-600" data-id="element-385">Thana:</span>{' '}
                    <span className="font-medium" data-id="element-386">
                      {formData.thana || 'Not provided'}
                    </span>
                  </div>
                  <div data-id="element-387">
                    <span className="text-gray-600" data-id="element-388">Union:</span>{' '}
                    <span className="font-medium" data-id="element-389">
                      {formData.union || 'Not provided'}
                    </span>
                  </div>
                  <div data-id="element-390">
                    <span className="text-gray-600" data-id="element-391">Village:</span>{' '}
                    <span className="font-medium" data-id="element-392">
                      {formData.village || 'Not provided'}
                    </span>
                  </div>
                  <div data-id="element-393">
                    <span className="text-gray-600" data-id="element-394">Station:</span>{' '}
                    <span className="font-medium" data-id="element-395">
                      {formData.station || 'Not provided'}
                    </span>
                  </div>
                  <div data-id="element-396">
                    <span className="text-gray-600" data-id="element-397">Phone:</span>{' '}
                    <span className="font-medium" data-id="element-398">
                      {formData.phone || 'Not provided'}
                    </span>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 rounded-lg p-4" data-id="element-399">
                <div className="flex items-center justify-between mb-3" data-id="element-400">
                  <h4 className="font-semibold text-gray-900" data-id="element-401">
                    Professional Details
                  </h4>
                  <button onClick={() => setCurrentStep(2)} className="text-sm text-[#1B5E20] hover:underline" data-id="element-402">
                    Edit
                  </button>
                </div>
                <div className="grid grid-cols-1 gap-3 text-sm" data-id="element-403">
                  <div data-id="element-404">
                    <span className="text-gray-600" data-id="element-405">Professions:</span>{' '}
                    <span className="font-medium capitalize" data-id="element-406">
                      {formData.professions.length > 0 ? formData.professions.map(p => p === 'Others' ? formData.otherProfession : p).join(', ') : 'Not provided'}
                    </span>
                  </div>
                  <div data-id="element-407">
                    <span className="text-gray-600" data-id="element-408">Conservation Group:</span>{' '}
                    <span className="font-medium" data-id="element-409">{formData.groupName}</span>
                  </div>
                </div>
              </div>
            </div>}
        </WizardForm>}

      {activeTab === 'approval' && <DataTable columns={approvalColumns} data={pendingApprovals} onView={row => console.log('View', row)} onApprove={row => alert(`Approved: ${row.name}`)} searchPlaceholder="Search pending approvals..." data-id="element-410" />}
    </div>;
}